import { Link, useParams } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { useProject } from '../hooks/useProjects';
import { EditWithAIButton } from '../components/ui/EditWithAIButton';

/**
 * Project create/edit (CLE-41).
 *
 * Projects are managed markdown-first: every change is made by the AI assistant,
 * which writes the Knowledge Base and re-derives the read-only database via the
 * sync engine. The direct edit form has been removed in favour of an
 * "Edit with AI" hand-off that pre-loads context into the chat.
 */
export function ProjectFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { data: existing, isLoading } = useProject(isEdit ? id : undefined);

  if (isEdit && isLoading) {
    return <div className="p-8 text-gray-500">Se încarcă proiectul…</div>;
  }

  const prompt = isEdit
    ? `Vreau să editez proiectul "${existing?.name ?? id}". ` +
      `Iată starea curentă — te rog întreabă-mă ce să modific, apoi actualizează-l:\n\n` +
      `- Nume: ${existing?.name ?? ''}\n` +
      `- Status: ${existing?.status ?? ''}\n` +
      `- Stare: ${existing?.health ?? ''}\n` +
      `- Descriere: ${existing?.description ?? '(niciuna)'}`
    : `Vreau să creez un proiect nou. Te rog cere-mi numele, statusul, ` +
      `starea, datele și bugetul, apoi creează-l în Knowledge Base.`;

  return (
    <div className="mx-auto max-w-2xl p-8">
      <Link
        to={isEdit ? `/projects/${id}` : '/projects'}
        className="text-sm text-blue-600 underline"
      >
        ← Înapoi
      </Link>
      <h1 className="mt-2 text-3xl font-bold text-gray-900">
        {isEdit ? 'Editează proiectul' : 'Proiect nou'}
      </h1>

      <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-600 text-white">
            <Sparkles size={18} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">Gestionat de asistentul AI</h2>
            <p className="mt-1 text-sm text-gray-600">
              Proiectele sunt editate prin asistent, astfel încât fiecare modificare este scrisă în
              Knowledge Base și sincronizată cu baza de date read-only. Folosește butonul de mai jos
              pentru a {isEdit ? 'edita acest proiect' : 'crea un proiect'} cu AI.
            </p>
          </div>
        </div>

        <div className="mt-5 flex gap-3">
          <EditWithAIButton prompt={prompt} label={isEdit ? 'Editează cu AI' : 'Creează cu AI'} />
          <Link
            to={isEdit ? `/projects/${id}` : '/projects'}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Anulează
          </Link>
        </div>
      </div>
    </div>
  );
}
